import { apiFetch } from "../../../context/AuthContext";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";

/**
 * Employee Actions API Service - ENHANCED WITH WORKFLOW & HISTORY
 * Handles all API calls for employee actions management
 */

class EmployeeActionsAPI {
  constructor(baseURL = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  async fetchWithAuth(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const defaultHeaders = {};
    // Only set Content-Type for JSON bodies (avoid for FormData/uploads)
    const hasBody = options && options.body !== undefined && options.body !== null;
    const isFormData = typeof FormData !== "undefined" && hasBody && options.body instanceof FormData;
    if (hasBody && !isFormData) {
      defaultHeaders["Content-Type"] = "application/json";
      // Ensure body is a string for JSON requests
      if (typeof options.body !== "string") {
        options = { ...options, body: JSON.stringify(options.body) };
      }
    }

    try {
      console.log(`🔵 API Request: ${options.method || "GET"} ${url}`);
      if (options.body) {
        console.log("📦 Request Body:", JSON.parse(options.body));
      }

      const response = await apiFetch(url, {
        ...options,
        // Every call through this class goes straight to the server —
        // never served out of the browser's HTTP cache. Without this,
        // a GET made right after a create/approve/reject can come back
        // from cache with the pre-change data, so the UI looks like the
        // change "didn't appear" even though the refetch call fired.
        cache: "no-store",
        headers: {
          ...defaultHeaders,
          ...(options.headers || {}),
        },
      });

      // Handle non-JSON responses
      const contentType = response.headers.get("content-type");
      let data;

      if (contentType && contentType.includes("application/json")) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { message: text };
      }

      if (!response.ok) {
        console.error(`🔴 API Error: ${response.status}`, data);
        const msg = (data && (data.message || data.error)) || `HTTP error! status: ${response.status}`;
        throw new Error(msg);
      }

      console.log(`🟢 API Success:`, data);
      return data;
    } catch (error) {
      console.error(`🔴 API Error [${endpoint}]:`, error);
      throw error;
    }
  }

  // ==================== EMPLOYEE ENDPOINTS ====================

  async getEmployees(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.clientIdentifier) {
      const endpoint = `/employees/clients/${filters.clientIdentifier}`;
      const response = await this.fetchWithAuth(endpoint);
      return Array.isArray(response) ? response : response.employees || [];
    }

    if (filters.department && filters.department !== "all") {
      queryParams.append("department", filters.department);
    }
    if (filters.performance && filters.performance !== "all") {
      queryParams.append("performance", filters.performance);
    }
    if (filters.searchTerm) {
      queryParams.append("search", filters.searchTerm);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/employees?${queryString}` : "/employees";

    const response = await this.fetchWithAuth(endpoint);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getAllClients() {
    const response = await this.fetchWithAuth("/employees/clients/all");
    return response.clients || response.data || response || [];
  }



  // In your employeeActionsAPI.js (document 3)

  async getEmployeesByClient(clientIdentifier) {
    if (!clientIdentifier) {
      throw new Error("Client identifier is required");
    }

    console.log(`🔵 Fetching employees for client: ${clientIdentifier}`);

    const response = await this.fetchWithAuth(
      `/employees/clients/${clientIdentifier}`
    );

    console.log(`🟢 Received response:`, response);

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }

    if (response.employees) {
      return response.employees;
    }

    if (response.data) {
      return Array.isArray(response.data) ? response.data : response.data.employees || [];
    }

    return [];
  }

  // `fields` defaults to "table" — this page (and every caller of this
  // method within the Changes page) only ever reads the ~20 columns
  // EMPLOYEE_TABLE_SELECT covers (name, contact info, department, salary,
  // status, reporting line). The backend used to always return the full
  // 87-column employee record for a manager's entire hierarchy on every
  // search keystroke/filter change; requesting the lean shape cuts both the
  // DB round trip and the JSON payload down substantially. Pass
  // fields="full" explicitly if a future caller genuinely needs every column.
  async getEmployeesByClientbymanager(clientIdentifier, myInfinityCode, viewType = null, fields = "table") {
    if (!clientIdentifier) {
      throw new Error("Client identifier is required");
    }

    // Use provided viewType, fall back to localStorage for backwards-compatibility
    const resolvedViewType = viewType || localStorage.getItem('managerViewType') || 'manager';

    console.log(`🔵 Fetching employees for client: ${clientIdentifier}/Admin1?managerInfinityCode=${myInfinityCode}&viewType=${resolvedViewType}&fields=${fields}`);

    const response = await this.fetchWithAuth(
      `/employees/clients/${clientIdentifier}/Admin1?managerInfinityCode=${myInfinityCode}&viewType=${resolvedViewType}&fields=${fields}`
    );

    console.log(`🟢 Received response:`, response);

    // Handle different response formats
    if (Array.isArray(response)) {
      return response;
    }

    if (response.employees) {
      return response.employees;
    }

    if (response.data) {
      return Array.isArray(response.data) ? response.data : response.data.employees || [];
    }

    return [];
  }

  /**
   * Get employees visible to the current user according to admin-level visibility rules
   * Server uses the authenticated user (from token) to resolve visibility. Optionally pass `clientName`.
   */
  // `fields` defaults to "table" for the same reason as
  // getEmployeesByClientbymanager above — every caller in this file only
  // reads table-level columns, and this is the admin-view employee fetch
  // that used to ship the full 87-column record for the whole client.
  async getEmployeesByVisibility(clientName, viewType, fields = "table") {
    const params = new URLSearchParams();
    if (clientName) params.set("clientName", clientName);
    if (fields) params.set("fields", fields);
    const query = params.toString() ? `?${params.toString()}` : "";
    const options = viewType
      ? { headers: { "x-view-type": viewType } }
      : {};
    const response = await this.fetchWithAuth(`/employees/visibility${query}`, options);

    // Response format: { success: true, count, client, employees }
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.employees) return response.employees;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.data && Array.isArray(response.data.employees)) return response.data.employees;
    return [];
  }

  /**
   * Get employees by admin level (uses payroll visibility endpoint)
   * Accepts an adminLevelId (or admin infinityCode) and optional workingCompany to further scope results.
   */
  async getEmployeesByAdminLevel(adminLevelId, workingCompany) {
    if (!adminLevelId) throw new Error("adminLevelId is required");
    const q = workingCompany ? `?workingCompany=${encodeURIComponent(workingCompany)}` : "";
    // Use payrollX mount used elsewhere in frontend
    const endpoint = `/payrollX/visibility/admin/${encodeURIComponent(adminLevelId)}/employees${q}`;
    const response = await this.fetchWithAuth(endpoint);

    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (response.data && Array.isArray(response.data)) return response.data;
    if (response.data && Array.isArray(response.data.data)) return response.data.data;
    if (response.data && response.data.employees) return response.data.employees;
    if (response.employees) return response.employees;
    return [];
  }

  async getEmployeeByInfinityCode(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employees/select/${infinityCode}`
    );
    return response.employee || response.data || response;
  }

  async getActiveEmployeeCount(clientName) {
    const response = await this.fetchWithAuth(`/employees/client/${clientName}/Active`);
    if (typeof response === "number") return response;
    if (response.count !== undefined) return response.count;
    if (response.data !== undefined) return response.data;
    return 0;
  }

  async getEmployeeCount(clientname) {
    const response = await this.fetchWithAuth(`/employees/client/${clientname}/count`);
    if (typeof response === "number") return response;
    if (response.count !== undefined) return response.count;
    if (response.data !== undefined) return response.data;
    return 0;
  }

  // ==================== DEPARTMENT ENDPOINTS ====================

  /**
   * List departments for a client/company from the departments API — the
   * same table the Departments admin page reads and writes. This is the
   * one place action forms and employee dropdowns should get department
   * names from, instead of each screen re-deriving its own list from
   * whatever employees happen to be loaded.
   * @param {string} clientName
   * @param {string} [workingCompany]
   * @param {boolean} [includeInactive]
   */
  async getDepartments(clientName, workingCompany, includeInactive = false) {
    if (!clientName) throw new Error("clientName is required");
    const params = new URLSearchParams({ clientName });
    if (workingCompany) params.append("workingCompany", workingCompany);
    if (includeInactive) params.append("includeInactive", "true");

    const response = await this.fetchWithAuth(`/api/departments?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== ACTION CRUD ENDPOINTS ====================

  async createAction(actionData) {
    console.log("📝 Creating action with data:", actionData);

    if (!actionData.infinityCode) throw new Error("infinityCode is required");
    if (!actionData.actionType) throw new Error("actionType is required");
    if (!actionData.effectiveDate) throw new Error("effectiveDate is required");
    if (!actionData.reason) throw new Error("reason is required");

    const response = await this.fetchWithAuth("/employee-actions", {
      method: "POST",
      body: JSON.stringify(actionData),
    });

    return response.data || response;
  }

  async getAllActions(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.status) queryParams.append("status", filters.status);
    if (filters.actionType)
      queryParams.append("actionType", filters.actionType);
    if (filters.department)
      queryParams.append("department", filters.department);
    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);
    if (filters.searchTerm)
      queryParams.append("searchTerm", filters.searchTerm);
    if (filters.clientName) queryParams.append("clientName", filters.clientName);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/employee-actions?${queryString}`
      : "/employee-actions";

    // The server scopes this list to the caller (manager view -> their own
    // hierarchy, admin view -> their admin-level visibility set) based on
    // this header — same convention as getEmployeesByVisibility() above.
    // Without it the server defaults to "admin" view, which is wrong for a
    // plain manager browsing in manager view.
    const options = filters.viewType
      ? { headers: { "x-view-type": filters.viewType } }
      : {};

    const response = await this.fetchWithAuth(endpoint, options);
    return Array.isArray(response) ? response : response.data || [];
  }

  async getActionById(id) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`);
    return response.data || response;
  }

  async getEmployeeActions(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/employee/${infinityCode}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  async updateAction(id, updateData) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`, {
      method: "PUT",
      body: JSON.stringify(updateData),
    });
    return response.data || response;
  }

  async deleteAction(id) {
    const response = await this.fetchWithAuth(`/employee-actions/${id}`, {
      method: "DELETE",
    });
    return response.data || response;
  }

  // ==================== WORKFLOW ENDPOINTS ====================

  async approveAction(id, approvalData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/approve`,
      {
        method: "PUT",
        body: JSON.stringify(approvalData),
      }
    );
    return response.data || response;
  }

  async rejectAction(id, rejectionData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/reject`,
      {
        method: "PUT",
        body: JSON.stringify(rejectionData),
      }
    );
    return response.data || response;
  }

  async processAction(id, processorData) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/process`,
      {
        method: "PUT",
        body: JSON.stringify(processorData),
      }
    );
    return response.data || response;
  }

  async bulkApproveActions(actionIds, approvedBy, comments) {
    const response = await this.fetchWithAuth(
      "/employee-actions/bulk/approve",
      {
        method: "POST",
        body: JSON.stringify({
          actionIds,
          approvedBy,
          comments,
        }),
      }
    );
    return response.data || response;
  }

  // ==================== NEW: WORKFLOW-SPECIFIC ENDPOINTS ====================

  async getActionWorkflow(id) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/workflow`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Approve a specific workflow step (Revision 3 engine).
   * @param {number} workflowInstanceId
   * @param {number} stepOrder
   * @param {string} approvedBy   — infinityCode of the approver
   * @param {string} [comments]
   */
  async approveWorkflowStep(workflowInstanceId, stepOrder, approvedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/${workflowInstanceId}/steps/${stepOrder}/approve`,
      {
        method: "PUT",
        body: JSON.stringify({ approvedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Reject a specific workflow step (Revision 3 engine).
   */
  async rejectWorkflowStep(workflowInstanceId, stepOrder, rejectedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/${workflowInstanceId}/steps/${stepOrder}/reject`,
      {
        method: "PUT",
        body: JSON.stringify({ rejectedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Approve the CURRENT pending step of a module record without the caller
   * having to know the workflowInstanceId/stepOrder ahead of time — the
   * server resolves the actual live step itself (workflowInstance.currentStepOrder),
   * so this can never target a stale/wrong step the way a client-side lookup
   * from a partially-populated pending-workflows map could.
   *
   * Also carries the admin/superadmin override: an admin calling this
   * approves every remaining step in one shot, whereas the plain
   * approveWorkflowStep() endpoint has no such bypass and 403s unless the
   * caller is the exact step assignee.
   */
  async approveActionByRecord(recordId, approvedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/by-record/ACTION/${recordId}/approve`,
      {
        method: "PUT",
        body: JSON.stringify({ approvedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Reject the CURRENT pending step of a module record — see
   * approveActionByRecord() above for why this is preferred over
   * rejectWorkflowStep() from the frontend.
   */
  async rejectActionByRecord(recordId, rejectedBy, comments = "") {
    const response = await this.fetchWithAuth(
      `/workflows/by-record/ACTION/${recordId}/reject`,
      {
        method: "PUT",
        body: JSON.stringify({ rejectedBy, comments }),
      }
    );
    return response.data || response;
  }

  /**
   * Fetch all pending approval steps for the current user
   * across ALL modules (unified inbox).
   * @param {string}   infinityCode
   * @param {number}   [adminLevelId]
   * @param {string}   [module]  — optional filter e.g. "ACTION"
   */
  async getPendingApprovals(infinityCode, adminLevelId, module) {
    const params = new URLSearchParams({ infinityCode });
    if (adminLevelId) params.append("adminLevelId", adminLevelId);
    if (module) params.append("module", module);
    const response = await this.fetchWithAuth(`/workflows/pending?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * List workflow definitions for the Configuration → Workflows tab.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  async listWorkflowDefinitions(clientName, workingCompany) {
    const params = new URLSearchParams({ clientName, workingCompany });
    const response = await this.fetchWithAuth(`/workflows/definitions?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Approval-path summary (module / subType / department + each step's order,
   * name and assignee type). Unlike listWorkflowDefinitions (admin-only, for
   * the Configuration → Workflows editor) this is readable by every member of
   * the company, so managers and admin-level users see the real configured
   * chain on the Changes page instead of the static fallback label.
   */
  async listWorkflowSummaries(clientName, workingCompany, module = "ACTION") {
    const params = new URLSearchParams({ clientName, workingCompany });
    if (module) params.set("module", module);
    const response = await this.fetchWithAuth(`/workflows/definitions/summary?${params}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * List company employees selectable as explicit workflow assignees.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  // `module` (ACTION|LEAVE|ADVANCEMENT|DOCUMENT|OVERTIME), when passed,
  // restricts the list server-side to employees whose user account holds an
  // admin level granting that module — see workflowRoutes.js's
  // /assignable-employees for the full reasoning. Omit it to get the
  // unfiltered company roster (used by callers that aren't the EXPLICIT
  // assignee picker).
  async listWorkflowAssignableEmployees(clientName, workingCompany, module = null) {
    const params = new URLSearchParams();
    if (clientName) params.set("clientName", clientName);
    if (workingCompany) params.set("workingCompany", workingCompany);
    if (module) params.set("module", module);
    const response = await this.fetchWithAuth(`/workflows/assignable-employees?${params.toString()}`);
    return Array.isArray(response) ? response : response.data || [];
  }

  /**
   * Client-wide employee list for the "reports to" / First Line Manager
   * picker. Unlike /workflows/assignable-employees (admin-only, meant for the
   * Workflows configuration assignee picker), this route is open to every
   * authenticated member of the company and is never narrowed by the
   * caller's admin-level visibility rules: any employee of the same company
   * may be chosen as someone's manager.
   * Returns [{ infinityCode, clientCode, sr, arabicName, departmentName }].
   */
  async listManagerOptions(clientName) {
    const params = new URLSearchParams();
    if (clientName) params.set("clientName", clientName);
    const response = await this.fetchWithAuth(`/employees/manager-options?${params.toString()}`);
    return Array.isArray(response) ? response : response?.data || [];
  }

  /**
   * Save / update a workflow definition from the Configuration → Workflows tab.
   * @param {object} definition  — { clientName, workingCompany, module, subType, targetEmployeeType, steps[] }
   */
  async saveWorkflowDefinition(definition) {
    const response = await this.fetchWithAuth("/workflows/definitions", {
      method: "POST",
      body: JSON.stringify(definition),
    });
    return response.data || response;
  }

  /**
   * Seed the default ACTION/ADVANCEMENT/DOCUMENT/LEAVE workflow definitions
   * for a company that has zero WorkflowDefinition rows yet. No-op
   * (returns { created: false, ... }) if the company already has definitions.
   * @param {string} clientName
   * @param {string} workingCompany
   */
  async ensureDefaultWorkflowDefinitions(clientName, workingCompany) {
    const response = await this.fetchWithAuth("/workflows/definitions/ensure-defaults", {
      method: "POST",
      body: JSON.stringify({ clientName, workingCompany }),
    });
    return response.data || response;
  }

  async getActionTimeline(id) {
    const response = await this.fetchWithAuth(
      `/employee-actions/${id}/timeline`
    );
    return response.data || response;
  }

  async getActionsByWorkflowStep(step) {
    const response = await this.fetchWithAuth(
      `/employee-actions/workflow/pending/${step}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== HISTORY ENDPOINTS ====================

  async getActionHistory(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/history/${infinityCode}`
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  async getEmployeeChangeSummary(infinityCode) {
    const response = await this.fetchWithAuth(
      `/employee-actions/employee/${infinityCode}/summary`
    );
    return response.data || response;
  }

  // ==================== STATISTICS ENDPOINTS ====================

  async getActionStats(filters = {}) {
    const queryParams = new URLSearchParams();

    if (filters.startDate) queryParams.append("startDate", filters.startDate);
    if (filters.endDate) queryParams.append("endDate", filters.endDate);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/employee-actions/stats?${queryString}`
      : "/employee-actions/stats";

    const response = await this.fetchWithAuth(endpoint);
    return response.data || response;
  }

  // ==================== MANAGER ENDPOINTS ====================

  /**
   * @param {string} infinityCode
   * @param {"all"|"manager"} [scope="all"] - "manager" restricts the server
   *   query to genuine assignee/alternate/delegate matches, dropping
   *   admin-authority-only matches (ADMIN_LEVEL/ADMIN_GENERAL). Pass this
   *   when the result feeds the MANAGER-VIEW list directly (see
   *   getPendingActionsByView below) — without it, someone who holds an
   *   admin level but has no management relationship to the requester at
   *   all would show up in that person's manager view instead of only the
   *   admin view. Leave it "all" (the default) for any other use, such as
   *   the admin-view union below, which deliberately wants admin-authority
   *   matches included.
   */
  async getManagerPendingActions(infinityCode, scope = "all") {
    // This route is gated by checkFeaturePermission('changesPage',
    // 'viewpendingapprovals'), which has a built-in bypass for a plain
    // manager role acting in THEIR OWN manager view (dbRole === 'manager'
    // && viewType !== 'admin' -> next()) — a manager doesn't need an
    // explicit admin-level grant just to see their own approval queue.
    // But that check reads viewType from the x-view-type header (or
    // query/body), defaulting to 'admin' when absent — and this call never
    // sent it, so the bypass never actually fired. A plain manager with no
    // admin level at all was falling through to the full admin-level
    // feature-permission check, failing it, and getting a 403 — meaning
    // their own dedicated pending-approvals inbox silently came back empty
    // (the caller here swallows the error) even though their step was
    // sitting there correctly assigned to them the whole time. This is
    // purely a PERMISSION-gate header, unrelated to the ?scope= query param
    // above — this call is also used (with scope left as "all") to fetch
    // the admin-view union further down, by a caller who may not hold a
    // plain "manager" role at all.
    const query = scope === "manager" ? "?scope=manager" : "";
    const response = await this.fetchWithAuth(
      `/employee-actions/manager/${infinityCode}/pending${query}`,
      { headers: { "x-view-type": "manager" } }
    );
    return Array.isArray(response) ? response : response.data || [];
  }

  // ==================== NEW: PAYROLL IMPACT ENDPOINTS ====================

  async getPayrollImpactPreview(
    infinityCode,
    effectiveDate,
    newSalary,
    oldSalary
  ) {
    const response = await this.fetchWithAuth(
      "/employee-actions/payroll-impact-preview",
      {
        method: "POST",
        body: JSON.stringify({
          infinityCode,
          effectiveDate,
          newSalary,
          oldSalary,
        }),
      }
    );
    return response.data || response;
  }

  /**
   * Fetch pending actions requested by a specific requester (you).
   * If `requestedBy` is not provided, attempt to read `infinityCode` from localStorage
   * or decode it from the stored JWT token payload.
   */
  async getPendingActions(requestedBy, infinityCodeFallback = null) {
    // Try provided param first
    let requester = requestedBy || infinityCodeFallback || localStorage.getItem("infinityCode");

    // If not available, try to decode token payload to extract an identifier
    if (!requester) {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split(".")[1]));
          requester = payload.infinityCode || payload.sub || payload.userId;
        } catch (e) {
          // ignore decoding errors
        }
      }
    }

    // Build query: filter pending actions and, if we have a requester, restrict to that requester
    const queryParams = new URLSearchParams();
    queryParams.append("status", "pending");
    if (requester) queryParams.append("requestedBy", requester);

    const endpoint = `/employee-actions?${queryParams.toString()}`;

    const response = await this.fetchWithAuth(endpoint);
    return Array.isArray(response) ? response : response.data || response;
  }

  /**
   * Get pending actions filtered according to the current view.
   * - viewRole: 'admin' | 'manager'
   * - options: { adminLevelId, clientIdentifier, clientName, managerInfinityCode, workingCompany }
   *
   * Strategy: fetch pending actions, fetch employees relevant to the view (using existing methods),
   * then return actions where the action's employee Infinity Code exists in the employee set.
   */
  async getPendingActionsByView(viewRole = "admin", options = {}) {
    // fetch pending actions first
    // For admin view we must fetch ALL pending actions (no requestedBy filter)
    //
    // The server now scopes this list to the caller (manager view -> their
    // own hierarchy, admin view -> their admin-level visibility set) based on
    // the x-view-type header, defaulting to "admin" when absent. Passing
    // viewRole through here is required — without it, a plain manager (no
    // admin-level visibility rules of their own) would get an EMPTY result
    // from this call, which used to feed the fallback intersection filter
    // below when the dedicated manager-inbox call further down comes back
    // empty for any reason.
    let pendingResp;
    try {
      pendingResp = await this.fetchWithAuth(`/employee-actions?status=pending`, {
        headers: { "x-view-type": viewRole === "manager" ? "manager" : "admin" },
      });
    } catch (e) {
      // fallback to generic getter
      pendingResp = await this.getPendingActions();
    }

    // Normalize pendingResp into an array of actions regardless of response shape
    const normalizeActions = (resp) => {
      if (!resp) return [];
      if (Array.isArray(resp)) return resp;
      if (Array.isArray(resp.data)) return resp.data;
      if (Array.isArray(resp.actions)) return resp.actions;
      if (Array.isArray(resp.items)) return resp.items;
      if (resp.data && Array.isArray(resp.data.actions)) return resp.data.actions;
      if (resp.data && Array.isArray(resp.data.data)) return resp.data.data;
      // try to find arrays on common keys
      for (const key of ["employees", "results", "list"]) {
        if (Array.isArray(resp[key])) return resp[key];
        if (resp.data && Array.isArray(resp.data[key])) return resp.data[key];
      }
      return [];
    };

    const actions = normalizeActions(pendingResp);

    // determine employee list based on viewRole
    let employees = [];

    // Actions where the CALLER (by infinityCode, admin level, or the
    // ADMIN_GENERAL pool) is the actual pending workflow step assignee right
    // now — fetched once up front and unioned into the final result below,
    // regardless of the employee-visibility filtering that follows. This
    // matters most for ADMIN view: an admin level configured as a workflow
    // step's approver is NOT necessarily also covered by that same admin
    // level's AdminLevelVisibilityRule employee-visibility scope — those are
    // two independent configuration concepts (one controls workflow
    // routing, the other controls what shows on the Employees page) — so
    // without this union, a request routed to an admin level for approval
    // could be completely invisible to that admin level if its visibility
    // rules don't happen to also cover the specific employee involved.
    let assignedActions = [];
    const myCode = options.managerInfinityCode || localStorage.getItem("infinityCode");
    if (myCode) {
      try {
        // MANAGER view must only receive matches where the caller is
        // genuinely the assignee (direct/second/department manager,
        // explicit assignee, or a person-type alternate) — not matches that
        // exist only because the caller also happens to hold an admin
        // level. Those admin-authority-only matches belong in the ADMIN
        // view instead, even when the caller has no management relationship
        // to the requester at all. ADMIN view keeps the unrestricted "all"
        // scope so its own union (below) still recovers admin-level steps
        // that fall outside the employee-visibility rules.
        const assigned = await this.getManagerPendingActions(
          myCode,
          viewRole === "manager" ? "manager" : "all"
        );
        assignedActions = Array.isArray(assigned) ? assigned : assigned?.data || [];
      } catch (err) {
        // ignore — falls back to visibility-only filtering
      }
    }
    const assignedActionIds = new Set(
      assignedActions.map((a) => a?.id).filter((id) => id !== null && id !== undefined)
    );

    if (viewRole === "admin") {
      if (options.adminLevelId) {
        employees = await this.getEmployeesByAdminLevel(
          options.adminLevelId,
          options.workingCompany
        );
      } else if (options.clientIdentifier) {
        employees = await this.getEmployeesByClient(options.clientIdentifier);
      } else {
        const company = options.workingCompany;
        employees = company
          ? await this.getEmployeesByVisibility(company, "admin")
          : await this.getEmployeesByVisibility(undefined, "admin");
      }

      // If we couldn't resolve any employees for admin view, return all pending actions
      if ((!employees || employees.length === 0) && assignedActionIds.size === 0) {
        return actions || [];
      }
    } else if (viewRole === "manager") {
      // options.managerInfinityCode should be set by the caller from profile data
      let managerCode =
        options.managerInfinityCode || localStorage.getItem("infinityCode");

      if (!managerCode) {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split(".")[1]));
            managerCode = payload.infinityCode || payload.sub || payload.userId;
          } catch (e) {
            // ignore
          }
        }
      }

      if (options.clientIdentifier && managerCode) {
        employees = await this.getEmployeesByClientbymanager(
          options.clientIdentifier,
          managerCode
        );
      } else {
        // fallback: fetch visibility and filter by managerCode (first-line)
        const company = options.workingCompany;
        const all = company
          ? await this.getEmployeesByVisibility(company, "admin")
          : await this.getEmployeesByVisibility(undefined, "admin");
        employees = (all || []).filter((e) => {
          if (!e) return false;
          const mCode = e.managerInfinityCode || e.directManager || e.lineManager || e.manager || e.managerId;
          if (mCode !== managerCode) return false;
          const firstLineMarker =
            e.managerType === "firstline" || e.lineManagerType === "firstline" || e.reportingLevel === 1 || e.reportingLevel === "1";
          return firstLineMarker || (!("managerType" in e) && !("lineManagerType" in e) && !("reportingLevel" in e));
        });
      }

      // NOTE: we deliberately do NOT return assignedActions directly here,
      // and do NOT union them into the result below for manager view (see
      // the union guard further down). Manager view visibility must be
      // strictly the manager's actual org-chart subordinates — even a
      // workflow step genuinely assigned to this manager (explicit
      // assignee, alternate approver) must not surface here for someone
      // outside that org chart, at any status, ever. That belongs in the
      // ADMIN view (or a dedicated approvals inbox) instead. See the
      // identical backend fix in leaveService.js, advancementService.js,
      // and documentRequestController.js's manager-scope helpers.
    } else {
      // unknown role, return empty
      return [];
    }

    const normalizeCode = (val) => {
      if (val === null || val === undefined) return null;
      return String(val).trim().toLowerCase();
    };

    const empSet = new Set(
      (employees || [])
        .map((e) => normalizeCode(e.infinityCode || e.InfinityCode || e.infinity || e.code || e.id))
        .filter(Boolean)
    );

    const filteredByVisibility = (actions || []).filter((action) => {
      const raw =
        action.infinityCode ||
        action.employeeInfinityCode ||
        (action.employee && (action.employee.infinityCode || action.employee.infinity || action.employee.id)) ||
        action.employeeId ||
        action.employee;
      const aCode = normalizeCode(raw);
      return !!aCode && empSet.has(aCode);
    });

    // Union with assignedActions (workflow-assignee-based, NOT
    // visibility-scoped) rather than just checking assignedActionIds against
    // `filteredByVisibility` — GET /employee-actions?status=pending is now
    // itself scoped to the caller's employee-visibility server-side, so an
    // action for an employee outside that visibility scope never reaches
    // `actions` above at all, even when the caller IS the actual workflow
    // step assignee for it. Deduped by action id.
    //
    // ADMIN view only: this union is exactly the leniency that must NOT
    // apply to manager view (see the note above candidateViews/manager
    // branch) — a manager must see only their actual org-chart
    // subordinates, full stop.
    if (viewRole !== "manager") {
      const byId = new Map();
      filteredByVisibility.forEach((a) => { if (a?.id != null) byId.set(a.id, a); });
      assignedActions.forEach((a) => { if (a?.id != null && !byId.has(a.id)) byId.set(a.id, a); });
      return [...byId.values()];
    }

    return filteredByVisibility;
  }

  // ==================== NEW: TEMPLATE ENDPOINTS ====================

  async getActionTemplates() {
    const response = await this.fetchWithAuth("/employee-actions/templates");
    return Array.isArray(response) ? response : response.data || [];
  }

  async createActionTemplate(templateData) {
    const response = await this.fetchWithAuth("/employee-actions/templates", {
      method: "POST",
      body: JSON.stringify(templateData),
    });
    return response.data || response;
  }

  // ==================== HELPER METHODS ====================

  /**
   * Get action type configuration
   */
  getActionTypeConfig(actionType) {
    const configs = {
      promote: {
        label: "Promote Employee",
        color: "#10b981",
        icon: "TrendingUp",
        requiresApproval: true,
        approvalLevels: 3,
      },
      demote: {
        label: "Demote Employee",
        color: "#ef4444",
        icon: "TrendingDown",
        requiresApproval: true,
        approvalLevels: 3,
      },
      transfer: {
        label: "Transfer Employee",
        color: "#3b82f6",
        icon: "MapPin",
        requiresApproval: true,
        approvalLevels: 3,
      },
      salary_change: {
        label: "Salary Adjustment",
        color: "#f59e0b",
        icon: "DollarSign",
        requiresApproval: true,
        approvalLevels: 3,
      },
      terminate: {
        label: "Terminate Employment",
        color: "#dc2626",
        icon: "UserX",
        requiresApproval: true,
        approvalLevels: 4,
      },
      suspend: {
        label: "Suspend Employee",
        color: "#f97316",
        icon: "UserMinus",
        requiresApproval: true,
        approvalLevels: 2,
      },
      rehire: {
        label: "Rehire Employee",
        color: "#8b5cf6",
        icon: "RefreshCw",
        requiresApproval: true,
        approvalLevels: 3,
      },
      contract: {
        label: "Contract Update",
        color: "#06b6d4",
        icon: "FileCheck",
        requiresApproval: true,
        approvalLevels: 3,
      },
      rotation: {
        label: "Job Rotation",
        color: "#ec4899",
        icon: "Repeat",
        requiresApproval: true,
        approvalLevels: 3,
      },
      review: {
        label: "Performance Review",
        color: "#14b8a6",
        icon: "ClipboardList",
        requiresApproval: false,
        approvalLevels: 1,
      },
      training: {
        label: "Training Assignment",
        color: "#6366f1",
        icon: "GraduationCap",
        requiresApproval: false,
        approvalLevels: 2,
      },
    };

    return configs[actionType] || null;
  }

  // ==================== BULK ACTIONS (Excel) ====================
  // Always sent in admin view: bulk actions are admin / admin-level only.

  bulkHeaders() {
    return { "x-view-type": "admin" };
  }

  async getBulkMeta() {
    const res = await this.fetchWithAuth("/employee-actions/bulk/meta", { headers: this.bulkHeaders() });
    return res.data;
  }

  async validateBulkActions(rows) {
    const res = await this.fetchWithAuth("/employee-actions/bulk/validate", {
      method: "POST",
      headers: this.bulkHeaders(),
      body: { rows },
    });
    return res.data;
  }

  async commitBulkActions({ rows, fileName, clientBatchKey }) {
    const res = await this.fetchWithAuth("/employee-actions/bulk/commit", {
      method: "POST",
      headers: this.bulkHeaders(),
      body: { rows, fileName, clientBatchKey },
    });
    return res.data;
  }

  async getBulkBatch(batchId) {
    const res = await this.fetchWithAuth(`/employee-actions/bulk/batches/${batchId}`, { headers: this.bulkHeaders() });
    return res.data;
  }

  async listBulkBatches(limit = 10) {
    const res = await this.fetchWithAuth(`/employee-actions/bulk/batches?limit=${limit}`, { headers: this.bulkHeaders() });
    return res.data;
  }

  /** Binary download (xlsx) → triggers a browser save. */
  async downloadFile(endpoint, fallbackName) {
    const response = await apiFetch(`${this.baseURL}${endpoint}`, {
      cache: "no-store",
      headers: this.bulkHeaders(),
    });
    if (!response.ok) {
      let msg = `HTTP error! status: ${response.status}`;
      try {
        const data = await response.json();
        msg = data?.message || data?.error || msg;
      } catch { /* not JSON */ }
      throw new Error(msg);
    }
    const blob = await response.blob();
    const disposition = response.headers.get("content-disposition") || "";
    const match = /filename="?([^";]+)"?/i.exec(disposition);
    const name = match ? match[1] : fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  downloadBulkTemplate(types, lang = "en") {
    const q = new URLSearchParams({ types: types.join(","), lang });
    return this.downloadFile(`/employee-actions/bulk/template?${q}`, "bulk-employee-actions.xlsx");
  }

  downloadBulkReport(batchId, lang = "en") {
    return this.downloadFile(`/employee-actions/bulk/batches/${batchId}/report?lang=${lang}`, `bulk-actions-results-${batchId}.xlsx`);
  }

  /**
   * Get status badge configuration
   */
  getStatusConfig(status) {
    const configs = {
      pending: { label: "Pending", color: "#f59e0b", bgColor: "#fef3c7" },
      approved: { label: "Approved", color: "#10b981", bgColor: "#dcfce7" },
      rejected: { label: "Rejected", color: "#ef4444", bgColor: "#fee2e2" },
      completed: { label: "Completed", color: "#3b82f6", bgColor: "#dbeafe" },
    };

    return (
      configs[status] || { label: status, color: "#64748b", bgColor: "#f1f5f9" }
    );
  }
}

// Create singleton instance
const employeeActionsAPI = new EmployeeActionsAPI();

export default employeeActionsAPI;